import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CustomTimePicker from "../FormFields/TiimePicker/TimePicker";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: "12px",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.04)",
  border: "1px solid #E2E8F0",
  overflow: "hidden",
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  padding: "4px 10px",
  minWidth: "48px",
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "6px",
  border: "1px solid #E2E8F0",
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    borderColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  "&:not(.Mui-selected)": {
    backgroundColor: "#FFFFFF",
    color: "#475467",
    "&:hover": {
      backgroundColor: "#F8FAFC",
      borderColor: "#94A3B8",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "8px 10px",
  fontSize: "0.8rem",
  borderBottom: "1px solid #F2F4F7",
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: "8px 10px",
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  color: "#475467",
  backgroundColor: "#F8FAFC",
  borderBottom: "1px solid #E2E8F0",
}));

const WeeklyPattern = ({
  weekNumber,
  initialAvailability,
  initialTimes,
  onDataChange,
}) => {
  const theme = useTheme();
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const defaultAvailability = {
    Monday: "None",
    Tuesday: "None",
    Wednesday: "None",
    Thursday: "None",
    Friday: "None",
    Saturday: "None",
    Sunday: "None",
  };

  const defaultTimes = {
    Monday: { start: "", end: "" },
    Tuesday: { start: "", end: "" },
    Wednesday: { start: "", end: "" },
    Thursday: { start: "", end: "" },
    Friday: { start: "", end: "" },
    Saturday: { start: "", end: "" },
    Sunday: { start: "", end: "" },
  };

  const [availability, setAvailability] = useState(
    initialAvailability || defaultAvailability
  );

  const [times, setTimes] = useState(initialTimes || defaultTimes);

  useEffect(() => {
    if (initialAvailability) {
      setAvailability(initialAvailability);
    }
    if (initialTimes) {
      setTimes(initialTimes);
    }
  }, [initialAvailability, initialTimes]);

  useEffect(() => {
    onDataChange?.({
      weekNumber,
      availability,
      times,
    });
  }, [availability, times, weekNumber, onDataChange]);

  const handleAvailabilityChange = (day, newValue) => {
    if (newValue !== null) {
      const updatedAvailability = {
        ...availability,
        [day]: newValue,
      };

      if (newValue !== "Partial") {
        const updatedTimes = {
          ...times,
          [day]: { start: "", end: "" },
        };
        setTimes(updatedTimes);
      }

      setAvailability(updatedAvailability);
    }
  };

  const handleTimeChange = (day, type, value) => {
    setTimes({
      ...times,
      [day]: {
        ...times[day],
        [type]: value,
      },
    });
  };

  const getAvailabilityColor = (status) => {
    if (status === "Fully") return "#22C55E";
    if (status === "Partial") return "#F59E0B";
    return "#94A3B8";
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E">
            Week {weekNumber}
          </Typography>
          <Typography variant="caption" color="#6B7280">
            Set availability for each day
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#22C55E",
              }}
            />
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.55rem" }}>
              Full
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#F59E0B",
              }}
            />
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.55rem" }}>
              Partial
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#94A3B8",
              }}
            />
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.55rem" }}>
              None
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledTableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableHeadCell width="10%">Day</StyledTableHeadCell>
              <StyledTableHeadCell width="32%">
                Availability
              </StyledTableHeadCell>
              <StyledTableHeadCell width="29%">Start Time</StyledTableHeadCell>
              <StyledTableHeadCell width="29%">End Time</StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {days.map((day) => {
              const isPartial = availability[day] === "Partial";
              const statusColor = getAvailabilityColor(availability[day]);

              return (
                <TableRow
                  key={day}
                  sx={{
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  <StyledTableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: statusColor,
                        }}
                      />
                      <Typography variant="body2" fontWeight={500} color="#1A1A2E" sx={{ fontSize: "0.75rem" }}>
                        {day}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <ToggleButtonGroup
                      value={availability[day]}
                      exclusive
                      onChange={(e, newValue) =>
                        handleAvailabilityChange(day, newValue)
                      }
                      aria-label={`${day} availability`}
                      size="small"
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        "& .MuiToggleButtonGroup-grouped": {
                          margin: 0,
                          border: "1px solid #E2E8F0",
                          "&:not(:first-of-type)": {
                            borderLeft: "1px solid #E2E8F0",
                          },
                        },
                      }}
                    >
                      <StyledToggleButton value="Fully" aria-label="full">
                        Full
                      </StyledToggleButton>
                      <StyledToggleButton value="Partial" aria-label="partial">
                        Partial
                      </StyledToggleButton>
                      <StyledToggleButton value="None" aria-label="none">
                        None
                      </StyledToggleButton>
                    </ToggleButtonGroup>
                  </StyledTableCell>
                  <StyledTableCell>
                    {isPartial && (
                      <CustomTimePicker
                        label="Start"
                        name="start"
                        value={times[day].start}
                        onChange={(e) =>
                          handleTimeChange(day, "start", e.target.value)
                        }
                        size="small"
                        fontSize="0.7rem"
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {isPartial && (
                      <CustomTimePicker
                        label="End"
                        name="end"
                        value={times[day].end}
                        onChange={(e) =>
                          handleTimeChange(day, "end", e.target.value)
                        }
                        size="small"
                        fontSize="0.7rem"
                      />
                    )}
                  </StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: "#F8FAFC",
          borderRadius: "8px",
          border: "1px solid #E2E8F0",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          color="#475467"
          sx={{ display: "block", mb: 0.5 }}
        >
          Availability Notes
        </Typography>
        <Typography variant="body2" color="#6B7280" sx={{ fontSize: "0.8rem" }}>
          No additional notes
        </Typography>
      </Box>
    </Box>
  );
};

export default WeeklyPattern;